import { Notify, unFormatCurrency, unFormatPercent } from 'utils';
import {
  ExistingPolicyType,
  FundAllocationType,
  ChargeType
} from 'store/modules/ExistingPolicies/types';

interface ExistingPoliciesProps extends ExistingPolicyType {
  settings: {
    chargesAllowPercentage: boolean;
    fundsAllowPercentage: boolean;
  };
  model_portfolio_charges: {
    cash_value: string;
    percentage_value: string;
    name: string;
  }[];
}

interface MPcharge {
  name: string;
  id: number;
  cash_value: string;
  percentage_value: string;
}
interface FilterChargesProps {
  charges: Array<ChargeType>;
}
interface FilterFundsProps {
  funds: Array<FundAllocationType>;
}
enum ChargeSources {
  'MONETRY' = 1,
  'PERCENTAGE' = 2
}

const filterCharges = ({ charges }: FilterChargesProps) => {
  const prepared = charges.map((charge: any) => {
    if (charge.cash_value === '-') {
      const chargeToSend = {
        kind: charge.kind,
        percentage_value: (
          Number(unFormatPercent(charge.percentage_value)) / 100
        ).toFixed(4),
        cash_value: null,
        source_value: 2
      };
      return chargeToSend;
    } else if (charge.percentage_value === '-') {
      const chargeToSend = {
        kind: charge.kind,
        cash_value: unFormatCurrency(charge.cash_value),
        percentage_value: null,
        source_value: 1
      };
      return chargeToSend;
    } else if (charge.source_value === ChargeSources.MONETRY) {
      const chargeToSend = {
        kind: charge.kind,
        cash_value: unFormatCurrency(charge.cash_value),
        percentage_value: null,
        source_value: charge.source_value
      };
      return chargeToSend;
    }

    return {
      kind: charge.kind,
      percentage_value: (
        Number(unFormatPercent(charge.percentage_value)) / 100
      ).toFixed(4),
      cash_value: null,
      source_value: charge.source_value
    };
  });

  const filterEmpty = prepared.filter((charge: any) => {
    const isValidCharge = charge.kind !== 98;
    if (charge.cash_value) {
      return Number(charge.cash_value) !== 0 && isValidCharge;
    }
    return Number(charge.percentage_value) !== 0 && isValidCharge;
  });

  return filterEmpty;
};

const filterFunds = ({ funds }: FilterFundsProps) => {
  const fundWithoutWrapCash = funds.filter(fundInstance => {
    return (
      fundInstance.model_portfolio || fundInstance.fund.name !== 'Wrap Cash'
    );
  });

  return fundWithoutWrapCash.map(fund => {
    if (fund.fund_value) {
      if (fund.model_portfolio) {
        return {
          fund_value: unFormatCurrency(fund.fund_value),
          model_portfolio: {
            ...fund.model_portfolio,
            id: fund.model_portfolio.id,
            type: fund.model_portfolio.type
          },
          source_value: 1
        };
      }
      return {
        ...fund,
        fund_value: unFormatCurrency(fund.fund_value),
        allocation: null,
        source_value: 1
      };
    }

    return {
      ...fund
    };
  });
};

const addFundCharge = (funds: any, appliedMPcharges: any) => {
  // Filter charge if any value is  provided
  const filteredCharge = appliedMPcharges.filter((charge: MPcharge) => {
    const cashValue = Number(unFormatCurrency(charge.cash_value));
    const percentageValue =
      Number(unFormatPercent(charge.percentage_value)) / 100;

    const isCashValueProvided = cashValue !== 0 && !isNaN(cashValue);

    const isPercentageValueProvided =
      percentageValue !== 0 && !isNaN(percentageValue);

    const isAnyValueProvided = isCashValueProvided || isPercentageValueProvided;

    return isAnyValueProvided;
  });

  const fundsWithMPCharges = funds.map((fund: FundAllocationType) => {
    const charge =
      fund.model_portfolio &&
      filteredCharge.find((charge: MPcharge) => {
        return charge.id === fund.model_portfolio.id;
      });

    const findCharge = () => {
      if (charge.source_value) {
        return {
          kind: 8, // PORTFOLIO MANAGMENT CHARGE
          percentage_value:
            charge.source_value === 1
              ? null
              : (
                  Number(unFormatPercent(charge.percentage_value)) / 100
                ).toFixed(4),
          cash_value:
            charge.source_value === 2
              ? null
              : unFormatCurrency(charge.cash_value),
          source_value: charge.source_value,
          manually_overriden: charge.manually_overriden
        };
      } else {
        return {
          kind: 8, // PORTFOLIO MANAGMENT CHARGE
          percentage_value:
            charge.percentage_value === '-'
              ? null
              : (
                  Number(unFormatPercent(charge.percentage_value)) / 100
                ).toFixed(4),
          cash_value:
            charge.cash_value === '-'
              ? null
              : unFormatCurrency(charge.cash_value),
          source_value: charge.percentage_value === '-' ? 1 : 2,
          manually_overriden: charge.manually_overriden
        };
      }
    };

    return {
      ...fund,
      charges: fund.model_portfolio && charge ? [findCharge()] : []
    };
  });

  return fundsWithMPCharges;
};

const findWrapCash = ({
  funds,
  key = 'fund_value'
}: {
  funds: Array<FundAllocationType>;
  key?: 'fund_value' | 'allocation';
}) => {
  const wrapCash = funds.filter(
    fundInstance => fundInstance.fund && fundInstance.fund.name === 'Wrap Cash'
  );

  if (wrapCash.length > 0) return unFormatCurrency(wrapCash[0][key]);
  return null;
};

export const formatPolicy = (policy: ExistingPoliciesProps) => {
  const policyCopy = { ...policy };

  const filterNonZeroValues = policyCopy.fund_allocations.filter(
    (fundAllocation: FundAllocationType) => {
      return unFormatCurrency(fundAllocation.fund_value) !== '0.00';
    }
  );

  return {
    id: policy.id,
    owner: { id: policy.owner },
    platform: policy.platform,
    type: policy.type,
    fund_value: unFormatCurrency(policy.fund_value),
    charges: filterCharges({
      charges: policy.charges
    }),
    fund_allocations: addFundCharge(
      filterFunds({ funds: filterNonZeroValues }),
      policyCopy.model_portfolio_charges
    ),
    number: policy.number,
    provider: policy.provider,
    transfer_value: unFormatCurrency(policy.transfer_value),
    wrap_amount: findWrapCash({ funds: policy.fund_allocations })
  };
};

export const checkFundExists = (existingFunds: any[], fund: any) => {
  return existingFunds.some(
    (addedFund: any) =>
      !addedFund.model_portfolio && addedFund.fund.id === fund.id
  );
};

export const fundActiveNotification = () => {
  Notify.warn(
    'It has not been possible to identify this holding.',
    'This may be because the holding is not available or marked as ‘inactive’ by our data provider, Morningstar'
  );
};

export const getCashFundIndex = (fields: any) => {
  return fields
    .getAll()
    .findIndex((field: any) => field.fund && field.fund.isin === 'CASH');
};
